// Angular
import { Directive, ElementRef, Renderer2, inject, input, AfterContentInit } from '@angular/core';

@Directive({
    selector: '[informationFillCheck]',
})
export class InformationFillCheckDirective implements AfterContentInit {

    // Private properties
    private el = inject(ElementRef);
    private renderer = inject(Renderer2);
    private iconElement: HTMLElement | null = null;

    // Input
    public readonly informationFillCheck = input<any | null>(null);

    /**
     * After Content Init 
     * @memberOf InformationFillCheckDirective
     */
    ngAfterContentInit(): void {
        this.handleUpdates(this.informationFillCheck());
    }

    /**
     * Main Logic to update UI
     * @param {*} inputData 
     * @returns {void} 
     * @memberOf InformationFillCheckDirective
     */
    private handleUpdates(inputData: any): void {
        const element = this.el.nativeElement;
        if (!inputData) return;
        const required = this.checkIfRequired(inputData.field, inputData.part);
        if (this.isDataEmpty(inputData.val)) {
            if (required === true) {
                this.renderer.addClass(element, 'required-info-bg'); // Red colour
            }
            if (required === false) {
                this.renderer.addClass(element, 'optional-info-bg'); // Yellow colour
                this.addWarningIcon(element);
            }
        }
    }

    /**
     * Add Warning Icon
     * @param {HTMLElement} parent 
     * @returns {void} 
     * @memberOf InformationFillCheckDirective
     */
    private addWarningIcon(parent: HTMLElement): void {
        if (this.iconElement && parent.contains(this.iconElement)) return;
        this.iconElement = this.renderer.createElement('i');
        const iconClasses = ['bi', 'bi-exclamation-triangle', 'fs-4', 'text-warning', 'info-check-icon'];
        iconClasses.forEach(cls => this.renderer.addClass(this.iconElement, cls));
        this.renderer.appendChild(parent, this.iconElement);
    }

    /**
     * Validation Helper
     * @param {*} val 
     * @returns {boolean} 
     * @memberOf InformationFillCheckDirective
     */
    private isDataEmpty(val: any): boolean {
        if (val === null || val === undefined) return true;
        if (Array.isArray(val) && val.length === 0) return true;
        const str = String(val).trim().toUpperCase();
        return str === '' || str === 'NA' || str === '--' || str === 'NULL';
    }

    /**
     * Check validation logic based on Part Type
     * @param {string} field 
     * @param {*} part 
     * @returns {boolean} 
     * @memberOf InformationFillCheckDirective
     */
    private checkIfRequired(field: string, part: any): boolean {
        if (!part || !part.stored) return false;
        const typeId = part.stored.type_id;  // 0=Used, 1=Economic, 2=Remanufactured, 3=New Original, 4=Styling
        const qCode = ((part.part_quality_code_sbr || '') + (part.part_quality_code_voh || '')).toUpperCase();

        switch (field) { // always required
            case 'name':
            case 'stock_number':
            case 'price':
            case 'quantity':
            case 'stock_date':
            case 'vat_type':
                return true;

            // Warning
            case 'location':
            case 'info':
            case 'manufacturing_brand':
            case 'manufacturing_ref':
            case 'manufacturing_ref_num':
            case 'manufacturing_number':
            case 'visual_number':
            case 'gearbox_type':
            case 'gearbox_num':
            case 'gearbox_ratio':
            case 'gearbox_desc':
            case 'engine_type':
            case 'engine_num':
            case 'engine_desc':
            case 'fits_year':
                return false;

            // Condtional 
            case 'manufacturer_original_no':  // OEM & Original Ref:
            case 'original_reference_no':
                return (typeId === 1) ? null : false;


            case 'quality_code': // Quality code
                return typeId === 0 ? true : null;

            case 'quality_remark': // Quality remark
                if (typeId === 0) return (qCode.includes('M') || qCode.includes('A*')) ? true : null;
                return null;

            // Normal
            case 'part_position':
            case 'multiple_vehicle_code':
            case 'alternative':
            default:
                return null;
        }
    }
}